import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { Ticket } from '../../services/api';

interface SlaTimerProps {
  ticket: Ticket;
}

function formatTimeRemaining(ms: number): string {
  const abs = Math.abs(ms);
  const hours = Math.floor(abs / (1000 * 60 * 60));
  const minutes = Math.floor((abs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return `${days}d ${remHours}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function SlaTimer({ ticket }: SlaTimerProps) {
  const [now, setNow] = useState(Date.now());

  // Tick every 30 seconds for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const metrics = ticket.metrics;
  if (!metrics) return null;

  const isResolved = ticket.status === 'resolved' || ticket.status === 'closed';

  // Resolved: show completion stats
  if (isResolved && metrics.resolution_time_minutes != null) {
    const breached = metrics.sla_breached;
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
        breached
          ? 'bg-red-50 text-red-700 border border-red-200'
          : 'bg-green-50 text-green-700 border border-green-200'
      }`}>
        {breached ? <AlertTriangle size={12} /> : <CheckCircle size={12} />}
        Resolved in {formatTimeRemaining(metrics.resolution_time_minutes * 60 * 1000)}
        {breached && ' (SLA breached)'}
      </div>
    );
  }

  // Active ticket: determine which deadline to show
  const hasResponded = ticket.first_response_at != null;
  const deadlineStr = hasResponded ? metrics.resolution_due_at : metrics.sla_due_at;
  if (!deadlineStr) return null;

  const deadline = new Date(deadlineStr).getTime();
  const remaining = deadline - now;
  const isOverdue = remaining < 0;
  const isUrgent = remaining > 0 && remaining < 60 * 60 * 1000; // < 1h

  const label = hasResponded ? 'Resolution due' : 'First response due';

  let colorClasses: string;
  let Icon: typeof Clock;

  if (isOverdue) {
    colorClasses = 'bg-red-50 text-red-700 border border-red-200';
    Icon = AlertTriangle;
  } else if (isUrgent) {
    colorClasses = 'bg-amber-50 text-amber-700 border border-amber-200';
    Icon = Clock;
  } else {
    colorClasses = 'bg-gray-50 text-gray-600 border border-gray-200';
    Icon = Clock;
  }

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${colorClasses}`}>
      <Icon size={12} />
      {label}: {isOverdue ? 'OVERDUE by ' : ''}{formatTimeRemaining(remaining)}
    </div>
  );
}
